import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Bell, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
};

export const NotificationBell = () => {
  const { token, role } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setNotes(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (_) { /* silent */ }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => {
    setNotes((ns) => ns.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await axios.post(`${API}/notifications/${id}/read`, {}, authHeader); } catch (_) {}
  };

  // Deep-link target for a notification, based on the signed-in role
  const targetFor = (n) => {
    const meta = n.meta || {};
    if (meta.lead_id) {
      if (role === "admin") return `/admin?lead=${meta.lead_id}`;
      if (role === "contractor") return `/portal?lead=${meta.lead_id}`;
    }
    if (meta.request_id) {
      if (role === "admin") return `/admin/repair-requests?repair=${meta.request_id}`;
      if (role === "contractor") return `/portal?repair=${meta.request_id}`;
      if (role === "agent") return `/agent?repair=${meta.request_id}`;
    }
    if (meta.contractor_id && role === "admin") return "/admin/contractors";
    return null;
  };

  const openNote = (n) => {
    if (!n.read) markRead(n.id);
    setOpen(false);
    const target = targetFor(n);
    if (target) navigate(target);
  };

  const markAll = async () => {
    setNotes((ns) => ns.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try { await axios.post(`${API}/notifications/read-all`, {}, authHeader); } catch (_) {}
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative grid place-items-center w-11 h-11 rounded-full border-2 border-[rgba(19,57,85,0.15)] text-[#133955] hover:border-[#54BCE6] transition-colors"
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="notification-unread-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-[#e5484d] text-white text-[11px] font-bold">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto rounded-2xl bg-white shadow-[0_16px_48px_rgba(19,57,85,0.18)] border border-[rgba(19,57,85,0.1)] z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(19,57,85,0.08)] sticky top-0 bg-white">
            <span className="font-display font-bold text-[#133955]">Notifications</span>
            {unread > 0 && (
              <button data-testid="notification-mark-all" onClick={markAll} className="inline-flex items-center gap-1 text-xs font-semibold text-[#133955] hover:underline">
                <Check size={13} /> Mark all read
              </button>
            )}
          </div>
          {notes.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-[#173042]/50" data-testid="notification-empty">You're all caught up.</p>
          ) : (
            <ul>
              {notes.map((n) => (
                <li key={n.id}>
                  <button
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => openNote(n)}
                    className={`w-full text-left px-4 py-3 border-b border-[rgba(19,57,85,0.06)] hover:bg-[#EAF7FC] transition-colors ${n.read ? "" : "bg-[#EAF7FC]/60"}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#54BCE6] shrink-0" />}
                      <div className={n.read ? "pl-4" : ""}>
                        <p className="text-sm text-[#173042]">{n.message}</p>
                        <p className="text-xs text-[#173042]/45 mt-0.5">{timeAgo(n.created_at)}</p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
